import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  type ChiliPiperLead,
  type ChiliPiperOpenMessage,
  type ChiliPiperShellMessage,
  chiliPiperIframeSandbox,
} from "../../../shared/chiliPiperShell";
import { CHILI_PIPER_ROUTE } from "../../../shared/reservedRoutes";
import { publicUrl } from "../../lib/tenantOrigin";

// The shell's ready message isn't ordered against the frame's load event (a
// cross-origin frame's postMessage routes through the browser process), so the
// no-shell verdict waits out this grace period after load and is cancelled if
// ready arrives. A 404 or an unconfigured public service never runs the shell,
// so it falls back here in ~1s instead of after the 15s availability timeout.
export const CHILI_PIPER_READY_GRACE_MS = 1000;

const Frame = styled("iframe")`
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
`;

type Props = {
  lead: ChiliPiperLead;
  onAvailability: (hasAvailability: boolean) => void;
  onBooked: () => void;
  onClosed: () => void;
  onError: () => void;
};

// Cross-origin booking shell (see services/shared/chiliPiperShell.ts). The lead
// goes out on load via origin-checked postMessage; ChiliPiper's lifecycle comes
// back as typed messages, so its script never touches the app origin or CSP.
export function ChiliPiperFrame({
  lead,
  onAvailability,
  onBooked,
  onClosed,
  onError,
}: Props) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const shellReady = useRef(false);
  const readyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shellUrl = useMemo(() => `${publicUrl()}${CHILI_PIPER_ROUTE}`, []);
  const shellOrigin = useMemo(() => new URL(shellUrl).origin, [shellUrl]);

  const boot = useCallback(() => {
    const message: ChiliPiperOpenMessage = {
      type: "3b-chili-piper-open",
      lead,
    };
    frameRef.current?.contentWindow?.postMessage(message, shellOrigin);
    if (shellReady.current) return;
    if (readyTimer.current !== null) clearTimeout(readyTimer.current);
    readyTimer.current = setTimeout(() => {
      readyTimer.current = null;
      onError();
    }, CHILI_PIPER_READY_GRACE_MS);
  }, [lead, shellOrigin, onError]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== shellOrigin) return;
      if (
        !frameRef.current ||
        event.source !== frameRef.current.contentWindow
      ) {
        return;
      }
      const data = event.data as ChiliPiperShellMessage | undefined;
      switch (data?.type) {
        case "3b-chili-piper-ready":
          shellReady.current = true;
          if (readyTimer.current !== null) {
            clearTimeout(readyTimer.current);
            readyTimer.current = null;
          }
          break;
        case "3b-chili-piper-availability":
          onAvailability(data.hasAvailability);
          break;
        case "3b-chili-piper-booked":
          onBooked();
          break;
        case "3b-chili-piper-closed":
          onClosed();
          break;
        case "3b-chili-piper-error":
          onError();
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [shellOrigin, onAvailability, onBooked, onClosed, onError]);

  useEffect(() => {
    return () => {
      if (readyTimer.current !== null) clearTimeout(readyTimer.current);
    };
  }, []);

  return (
    <Frame
      ref={frameRef}
      src={shellUrl}
      title="Book a demo"
      sandbox={chiliPiperIframeSandbox}
      onLoad={boot}
    />
  );
}
